import { Injectable, signal, WritableSignal, computed, inject } from '@angular/core';
import { Client } from 'typesense';
import { environment } from '../../environments/environment';
import { ProductResume } from '../product/product.types';
import { LocalStorageService } from './local-storage.service';

const RECENT_SEARCHES_STORAGE_KEY = 'navego_recent_searches';
const RECENT_SEARCHES_LIMIT = 5;

/**
 * Flat document stored in Typesense. Fields are indexed for search/filter/facet/sort.
 * `product_data` contains the full product as a JSON string (not indexed).
 */
export interface TypesenseDocument {
  id: string;
  name: string;
  description_short?: string;
  producer_name?: string;
  producer_id?: string;
  category_id: string;
  category_name: string;
  subcategory_id: string;
  subcategory_name: string;
  tag_ids?: string[];
  tag_names?: string[];
  is_subscription: boolean;
  is_a_la_carte: boolean;
  is_on_sale?: boolean;
  price: number;
  original_price?: number;
  position?: number;
  product_url: string;
  img: string;
  product_data?: string;
  /**
   * Delivery days this product has a cut-off rule for, lowercase ("tuesday"), empty on a
   * product with no rule (DEV-11469). Only the RULE is indexed, never the resolved state: the
   * index is one document per product shared by every customer, and whether a product is
   * closed depends on whose delivery it is.
   *
   * Stored but absent from the collection schema, so it comes back on the document and cannot
   * be used in `filter_by` — which this does not need, but a future "hide closed products"
   * facet would.
   */
  productCutOffDeliveryDays?: string[];
}

/**
 * Single stockInfo entry mirrored from the product detail backend payload.
 * `limit` is a negative buffer (e.g. -5 means "stop selling 5 units before zero").
 */
export interface StockInfoEntry {
  productId: number;
  attribute?: { id: number; value: { id: number } };
  variantId: number;
  packageId?: number;
  stock: number;
  limit: number;
  hasNotify?: boolean;
}

/**
 * Rich product structure parsed from `product_data` JSON field.
 * Contains nested objects needed for frontend rendering.
 */
export interface TypesenseProduct {
  id: number;
  name: string;
  price: number;
  originalPrice?: number;
  productUrl: string;
  img: string;
  isSubscription: boolean;
  isALaCarte: boolean;
  isBundle?: boolean;
  category: { id: number; name: string };
  subcategory: { id: number; name: string };
  subcategorySon?: { id: number; name: string };
  producer?: { id: number; name: string };
  tags?: { id: number; name: string; icon: string; isFeatured?: boolean }[];
  badges?: { id: number; name: string }[];
  attributes?: any[];
  packages?: any[];
  description?: { short: string; long: string };
  display?: string;
  stockInfo?: StockInfoEntry[];
}

const LOW_STOCK_THRESHOLD = 9;

export interface SearchSuggestion {
  id: string;
  name: string;
  type: 'product' | 'category' | 'producer';
  img?: string;
  productUrl?: string;
  highlight?: string;
  price?: number;
  originalPrice?: number;
  producerName?: string;
  isSubscription?: boolean;
  isALaCarte?: boolean;
  isBundle?: boolean;
  packages?: any[];
  attributes?: any[];
  canQuickAdd?: boolean;
  variantId?: number;
  packageId?: number;
  /**
   * Units the selected package puts in the order per add ("8 count" → 8). That, not 1, is
   * what has to fit in stock before the card can be shown as buyable.
   */
  packageQuantity?: number;
  attributeId?: number;
  attributeValueId?: number;
  parentProductId?: string;
  variantLabel?: string;
  /** Raw stockInfo from backend; used during expansion, stripped from final cards. */
  stockInfo?: StockInfoEntry[];
  /** Derived per-variant stock state attached to each final card. */
  stock?: { available: number; isOut: boolean; isLow: boolean };
  /** The indexed cut-off rule; what decides whether this card is worth resolving at all. */
  cutOffDeliveryDays?: string[];
  /** Resolved against the catalog by the search bar, only for the cards whose rule applies. */
  isCutOffClosed?: boolean;
  cutOffNotice?: string;
}

export interface SearchFacet {
  field: string;
  counts: { value: string; count: number }[];
}

export interface SearchResult {
  products: ProductResume[];
  totalFound: number;
  facets: SearchFacet[];
  searchTime: number;
}

export function isSimpleOneTimeProduct(product: {
  isALaCarte?: boolean;
  isSubscription?: boolean;
  isBundle?: boolean;
  attributes?: any[];
  packages?: any[];
}): boolean {
  return !!(
    product.isALaCarte &&
    !product.isBundle &&
    !product.attributes?.length &&
    (product.packages?.length ?? 0) <= 1
  );
}

/**
 * Compute per-variant stock state matching product-card-v2's OOS rule.
 * `limit` is a negative buffer; OOS when `limit === 0` or `stock - 1 < limit`.
 * Available units sellable = `stock - limit` (e.g. stock 3, limit -5 → 8 available).
 */
export function evaluateStockForSuggestion(suggestion: SearchSuggestion): { available: number; isOut: boolean; isLow: boolean } | undefined {
  const stockInfo = suggestion.stockInfo;
  if (!stockInfo?.length) return undefined;

  // Match the lookup keys used by product-card-v2:
  //   key = productId  (no attribute), or `${productId}_${attrId}_${attrValueId}` (variant)
  const parentId = parseInt(suggestion.parentProductId || suggestion.id, 10);
  const entry = stockInfo.find(e => {
    if (e.productId !== parentId) return false;
    if (suggestion.attributeId && suggestion.attributeValueId) {
      return e.attribute?.id === suggestion.attributeId
        && e.attribute?.value?.id === suggestion.attributeValueId;
    }
    return !e.attribute;
  });

  if (!entry) return undefined;

  // One add of a package card reserves the whole package ("8 count" → 8 units), so that is
  // what has to fit in stock — not a single unit. The shop card applies the same multiplier
  // before deciding it is out of stock (product-card-v2.component.ts:715) and drops packages
  // that no longer fit from its selector entirely (:1011). Without it, a large package
  // sitting on a small remainder rendered as perfectly buyable here, and the add then failed
  // silently against the backend's own stock check.
  const unitsPerAdd = suggestion.packageQuantity && suggestion.packageQuantity > 0
    ? suggestion.packageQuantity
    : 1;

  const newStockAfterQty = entry.stock - unitsPerAdd;
  const isOut = newStockAfterQty < 0
    ? (entry.limit === 0 || newStockAfterQty < entry.limit)
    : false;

  // Sellable units now (before any cart additions). When limit is 0 the buffer is strict,
  // so you can only sell down to zero. When limit is negative it acts as a buffer below zero.
  const availableUnits = entry.limit === 0
    ? Math.max(0, entry.stock)
    : Math.max(0, entry.stock - entry.limit);
  // Counted in whole packages, so "Only N left" measures what the card actually adds.
  // Floor because a partial package cannot be bought (3 units left of a "2 count" → 1).
  const available = Math.floor(availableUnits / unitsPerAdd);
  const isLow = !isOut && available > 0 && available < LOW_STOCK_THRESHOLD;

  return { available, isOut, isLow };
}

/**
 * After expansion, derive per-card stock state from the parent's stockInfo and
 * strip the raw stockInfo so it doesn't leak into final card props.
 */
export function attachStockState(suggestions: SearchSuggestion[]): SearchSuggestion[] {
  return suggestions.map(s => {
    const stock = evaluateStockForSuggestion(s);
    const { stockInfo: _drop, ...rest } = s;
    return stock ? { ...rest, stock } : rest;
  });
}

/**
 * A package's indexed price is per unit, so its real price is price × quantity — the same
 * arithmetic the shop card applies (product-card-v2.component.ts:784) and quick-add repeats
 * (search-bar.component.ts:519). Rounded to cents because package quantities are not always
 * whole ("1/2 lb" is 0.5, "4 oz" is 0.25): 3.99 × 0.5 would otherwise reach the card as
 * 1.995. Math.round to cents matches what `| currency` renders on the shop card for every
 * fractional package in the index, so both surfaces show the same amount.
 */
function packagePrice(unitPrice: number, quantity: number): number {
  return Math.round(unitPrice * quantity * 100) / 100;
}

export function expandSuggestions(suggestions: SearchSuggestion[]): SearchSuggestion[] {
  const expanded: SearchSuggestion[] = [];

  for (const suggestion of suggestions) {
    const isSubscriptionOnly = suggestion.isSubscription && !suggestion.isALaCarte;
    if (isSubscriptionOnly || suggestion.isBundle) {
      expanded.push(suggestion);
      continue;
    }

    // Expand by attributes (takes priority over packages)
    if (suggestion.attributes?.length) {
      for (const attribute of suggestion.attributes) {
        const values = attribute.values || [];
        for (const value of values) {
          if (!value.variantId) continue;
          expanded.push({
            ...suggestion,
            id: `${suggestion.id}_attr_${value.variantId}`,
            parentProductId: suggestion.id,
            name: `${suggestion.name} - ${value.name}`,
            img: value.image || suggestion.img,
            price: value.price ?? suggestion.price,
            originalPrice: value.originalPrice ?? suggestion.originalPrice,
            variantId: value.variantId,
            attributeId: attribute.id,
            attributeValueId: value.id,
            variantLabel: value.name,
            canQuickAdd: !!suggestion.isALaCarte && !suggestion.isBundle,
            attributes: [],
            packages: [],
          });
        }
      }
      continue;
    }

    // Expand by packages — single-package products included, so the packageId
    // always travels with the suggestion (ELIO-6622: quick-add shipped no package)
    if (suggestion.packages?.length) {
      for (const pkg of suggestion.packages) {
        // The card takes both the price and the discount from the selected package alone —
        // `variant.originalPrice = pack.originalPrice * pack.quantity`, then `isDiscounted`
        // against the package's own price — so a package with no originalPrice carries no
        // strike-through, rather than falling back to the parent's. Guarded on
        // `typeof quantity` and not the card's `pack?.quantity >= 0`, which is also true for
        // a null quantity and would price the package at $0.00.
        const packQty = typeof pkg.quantity === 'number' ? pkg.quantity : null;
        expanded.push({
          ...suggestion,
          id: `${suggestion.id}_pkg_${pkg.id}`,
          parentProductId: suggestion.id,
          name: `${suggestion.name} - ${pkg.name}`,
          packageId: +pkg.id,
          packageQuantity: packQty ?? undefined,
          price: packQty !== null && pkg.price != null
            ? packagePrice(pkg.price, packQty)
            : pkg.price ?? suggestion.price,
          originalPrice: packQty !== null
            ? (pkg.originalPrice ? packagePrice(pkg.originalPrice, packQty) : undefined)
            : pkg.originalPrice ?? suggestion.originalPrice,
          variantLabel: pkg.name,
          canQuickAdd: !!suggestion.isALaCarte && !suggestion.isBundle,
          attributes: [],
          packages: [],
        });
      }
      continue;
    }

    // Simple product — pass through
    expanded.push(suggestion);
  }

  return expanded;
}

@Injectable({
  providedIn: 'root'
})
export class TypesenseSearchService {
  private client: Client;
  private collection: string;
  private querySuggestionsCollection: string;
  private localStorage = inject(LocalStorageService);

  // Signals for reactive state management
  searchQuery: WritableSignal<string> = signal('');
  searchResults: WritableSignal<SearchResult | null> = signal(null);
  suggestions: WritableSignal<SearchSuggestion[]> = signal([]);
  /**
   * How many documents the last autocomplete query matched in total, and how many of them
   * actually came back under `per_page`. Both are raw document counts on purpose:
   * `suggestions()` is post-expansion, so one document can become several variant cards and
   * its length can exceed the number of documents shown. Comparing these two is the only
   * reliable way to answer "are there more results than the dropdown is showing?".
   */
  suggestionsTotalFound: WritableSignal<number> = signal(0);
  suggestionsReturnedCount: WritableSignal<number> = signal(0);
  popularProducts: WritableSignal<SearchSuggestion[]> = signal([]);
  popularSearches: WritableSignal<string[]> = signal([]);
  recentSearches: WritableSignal<string[]> = signal([]);
  querySuggestions: WritableSignal<string[]> = signal([]);
  isSearching: WritableSignal<boolean> = signal(false);
  searchError: WritableSignal<string | null> = signal(null);

  // Computed signals
  hasResults = computed(() => {
    const results = this.searchResults();
    return results !== null && results.totalFound > 0;
  });

  hasSuggestions = computed(() => this.suggestions().length > 0);

  constructor() {
    const typesenseConfig = environment.apis.typesense;

    this.client = new Client({
      nodes: [{
        host: typesenseConfig.host,
        port: typesenseConfig.port,
        protocol: typesenseConfig.protocol
      }],
      apiKey: typesenseConfig.apiKey,
      connectionTimeoutSeconds: 2
    });

    this.collection = typesenseConfig.collection;
    this.querySuggestionsCollection = typesenseConfig.querySuggestionsCollection;
  }

  async getAutocompleteSuggestions(query: string, limit: number = 6): Promise<SearchSuggestion[]> {
    if (!query || query.length < 2) {
      this.suggestions.set([]);
      this.suggestionsTotalFound.set(0);
      this.suggestionsReturnedCount.set(0);
      return [];
    }

    try {
      const searchParameters = {
        q: query,
        // subcategory_name added so searching "yogurt" surfaces products in the yogurt subcategory,
        // not only those whose product name contains "yogurt".
        query_by: 'name,producer_name,category_name,subcategory_name,description_short',
        // Heavy name weight so a name hit beats a subcategory-only hit when the term is also
        // a category (e.g. "milk" — the product literally named Milk should outrank cheeses
        // that happen to live in the Milk subcategory).
        query_by_weights: '20,4,2,2,1',
        // Boost matches earlier in the field — "Apple Juice" outranks "Sparkling Apple"
        // when searching "apple". Requires Typesense >= 0.25.
        prioritize_token_position: true,
        prefix: 'true',
        num_typos: '1',
        per_page: limit,
        highlight_full_fields: 'name',
        highlight_start_tag: '<mark>',
        highlight_end_tag: '</mark>'
      };

      const result = await this.client
        .collections(this.collection)
        .documents()
        .search(searchParameters);

      const suggestions: SearchSuggestion[] = (result.hits || []).map((hit: any) => {
        const doc = hit.document as TypesenseDocument;
        const highlight = hit.highlights?.find((h: any) => h.field === 'name');
        const richProduct = this.parseProductData(doc);

        return {
          id: doc.id,
          name: doc.name,
          type: 'product' as const,
          img: doc.img,
          productUrl: doc.product_url,
          highlight: highlight?.snippet || doc.name,
          price: doc.price,
          originalPrice: doc.original_price,
          producerName: doc.producer_name,
          isSubscription: doc.is_subscription,
          isALaCarte: doc.is_a_la_carte,
          isBundle: richProduct?.isBundle ?? false,
          packages: richProduct?.packages,
          attributes: richProduct?.attributes,
          canQuickAdd: richProduct
            ? isSimpleOneTimeProduct(richProduct)
            : isSimpleOneTimeProduct({
                isALaCarte: doc.is_a_la_carte,
                isSubscription: doc.is_subscription,
                isBundle: false,
                attributes: [],
                packages: []
              }),
          stockInfo: richProduct?.stockInfo,
          cutOffDeliveryDays: doc.productCutOffDeliveryDays
        };
      });

      // Generate query suggestions from product names
      const querySuggestions = this.generateQuerySuggestions(query, result.hits || []);
      this.querySuggestions.set(querySuggestions);

      this.suggestionsTotalFound.set(result.found ?? 0);
      this.suggestionsReturnedCount.set((result.hits || []).length);

      const expanded = attachStockState(expandSuggestions(suggestions));
      const reranked = this.rerankByNameMatch(expanded, query);
      this.suggestions.set(reranked);
      return reranked;
    } catch (error) {
      this.suggestions.set([]);
      this.suggestionsTotalFound.set(0);
      this.suggestionsReturnedCount.set(0);
      return [];
    }
  }

  /**
   * Promotes products whose name starts with the query, then word-boundary matches, then the rest.
   * Stable within each tier (preserves Typesense's score order) so we keep relevance signal where
   * the heuristic doesn't apply.
   */
  private rerankByNameMatch<T extends { name?: string }>(items: T[], query: string): T[] {
    const q = (query || '').trim().toLowerCase();
    if (!q) return items;

    const escaped = q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const wordBoundary = new RegExp(`(?:^|[\\s:\\-])${escaped}`, 'i');

    const tier = (item: T): number => {
      const name = (item.name || '').toLowerCase();
      if (name.startsWith(q)) return 0;
      if (wordBoundary.test(item.name || '')) return 1;
      if (name.includes(q)) return 2;
      return 3;
    };

    return items
      .map((item, idx) => ({ item, idx, t: tier(item) }))
      .sort((a, b) => a.t - b.t || a.idx - b.idx)
      .map(x => x.item);
  }

  async search(
    query: string,
    options: {
      page?: number;
      perPage?: number;
      filters?: string;
      sortBy?: string;
      facetBy?: string[];
    } = {}
  ): Promise<SearchResult> {
    const {
      page = 1,
      perPage = 20,
      filters = '',
      sortBy = '',
      facetBy = ['category_name', 'subcategory_name', 'producer_name', 'tag_names', 'is_on_sale']
    } = options;

    this.isSearching.set(true);
    this.searchError.set(null);
    this.searchQuery.set(query);

    try {
      const searchParameters: Record<string, string | number | boolean> = {
        q: query || '*',
        // subcategory_name added (weight 2) for child-category matching parity with autocomplete.
        query_by: 'name,description_short,producer_name,tag_names,subcategory_name,category_name',
        query_by_weights: '20,2,4,2,2,1',
        prioritize_token_position: true,
        prefix: 'true',
        num_typos: '2',
        typo_tokens_threshold: '1',
        page: String(page),
        per_page: String(perPage),
        highlight_full_fields: 'name',
        highlight_start_tag: '<mark>',
        highlight_end_tag: '</mark>',
        facet_by: facetBy.join(',')
      };

      if (filters) {
        searchParameters['filter_by'] = filters;
      }

      if (sortBy) {
        searchParameters['sort_by'] = sortBy;
      }

      const result = await this.client
        .collections(this.collection)
        .documents()
        .search(searchParameters);

      const transformed = this.transformToProductResume(result.hits || []);
      // Tier products by how cleanly the query hits the name — Typesense's _text_match buckets
      // tie too easily when many products match via subcategory/description.
      const products = this.rerankByNameMatch(transformed, query);
      const facets = this.transformFacets(result.facet_counts || []);

      const searchResult: SearchResult = {
        products,
        totalFound: result.found || 0,
        facets,
        searchTime: result.search_time_ms || 0
      };

      this.searchResults.set(searchResult);
      this.isSearching.set(false);

      return searchResult;
    } catch (error: any) {
      this.searchError.set(error.message || 'Search failed');
      this.isSearching.set(false);

      const emptyResult: SearchResult = {
        products: [],
        totalFound: 0,
        facets: [],
        searchTime: 0
      };

      this.searchResults.set(emptyResult);
      return emptyResult;
    }
  }

  /**
   * Fetches products by their ids as `SearchSuggestion`s (the same model the
   * search dropdown product cards use) WITHOUT mutating the global search state
   * (searchResults/searchQuery/isSearching). Used by the cart-promotions modal
   * (DEV-9447) to render the products a promotion requires the customer to add.
   *
   * Note: Typesense is keyed by `productId` (variant ids are not indexed), so
   * promotions must supply `productId`; the `variantId` is used elsewhere to add
   * the exact variant.
   */
  async getProductSuggestionsByIds(
    ids: (number | string)[],
    variantIds?: (number | string)[],
  ): Promise<SearchSuggestion[]> {
    if (!ids?.length) return [];
    const suggestions = await this.#searchPromotionSuggestions(
      `id:[${ids.join(',')}]`,
      Math.min(ids.length, 250),
    );
    // Promotions target an exact variant (DEV-9447): keep only the requested
    // variant cards, plus simple products (no variantId) which have a single card.
    if (variantIds?.length) {
      const want = new Set(variantIds.map((v) => String(v)));
      return suggestions.filter((s) => s.variantId == null || want.has(String(s.variantId)));
    }
    return suggestions;
  }

  /**
   * Products a category-based promotion can be filled with (DEV-9447). Matches the
   * category id against BOTH `category_id` and `subcategory_id` so a top-level
   * category also surfaces its subcategory products (hierarchical, mirroring the
   * shop filter). Capped to `limit` (default 12).
   */
  async getProductSuggestionsByCategories(
    categoryIds: (number | string)[],
    limit = 12,
  ): Promise<SearchSuggestion[]> {
    if (!categoryIds?.length) return [];
    const list = categoryIds.join(',');
    return this.#searchPromotionSuggestions(
      `(category_id:[${list}] || subcategory_id:[${list}])`,
      limit,
    );
  }

  /** Products carrying any of the given tags for a tag-based promotion (max `limit`). */
  async getProductSuggestionsByTags(
    tagIds: (number | string)[],
    limit = 12,
  ): Promise<SearchSuggestion[]> {
    if (!tagIds?.length) return [];
    return this.#searchPromotionSuggestions(`tag_ids:[${tagIds.join(',')}]`, limit);
  }

  /** Shared Typesense fetch for promotion product suggestions (side-effect free). */
  async #searchPromotionSuggestions(filterBy: string, perPage: number): Promise<SearchSuggestion[]> {
    try {
      const result = await this.client
        .collections(this.collection)
        .documents()
        .search({
          q: '*',
          query_by: 'name',
          filter_by: filterBy,
          per_page: perPage,
        });
      return attachStockState(expandSuggestions(this.#mapHitsToSuggestions(result.hits || [])));
    } catch (error) {
      return [];
    }
  }

  /** Maps raw Typesense hits to product `SearchSuggestion`s (mirrors the autocomplete mapping). */
  #mapHitsToSuggestions(hits: any[]): SearchSuggestion[] {
    return (hits || []).map((hit: any) => {
      const doc = hit.document as TypesenseDocument;
      const richProduct = this.parseProductData(doc);
      return {
        id: doc.id,
        name: doc.name,
        type: 'product' as const,
        img: doc.img,
        productUrl: doc.product_url,
        price: doc.price,
        originalPrice: doc.original_price,
        producerName: doc.producer_name,
        isSubscription: doc.is_subscription,
        isALaCarte: doc.is_a_la_carte,
        isBundle: richProduct?.isBundle ?? false,
        packages: richProduct?.packages,
        attributes: richProduct?.attributes,
        canQuickAdd: richProduct
          ? isSimpleOneTimeProduct(richProduct)
          : isSimpleOneTimeProduct({
              isALaCarte: doc.is_a_la_carte,
              isSubscription: doc.is_subscription,
              isBundle: false,
              attributes: [],
              packages: [],
            }),
        stockInfo: richProduct?.stockInfo,
      };
    });
  }

  buildFilterString(filters: {
    categories?: string[];
    subcategories?: string[];
    producers?: string[];
    tags?: string[];
    isOnSale?: boolean;
    priceMin?: number;
    priceMax?: number;
  }): string {
    const filterParts: string[] = [];

    if (filters.categories?.length) {
      filterParts.push(`category_name:[${filters.categories.map(c => `\`${c}\``).join(',')}]`);
    }

    if (filters.subcategories?.length) {
      filterParts.push(`subcategory_name:[${filters.subcategories.map(s => `\`${s}\``).join(',')}]`);
    }

    if (filters.producers?.length) {
      filterParts.push(`producer_name:[${filters.producers.map(p => `\`${p}\``).join(',')}]`);
    }

    if (filters.tags?.length) {
      filterParts.push(`tag_names:[${filters.tags.map(t => `\`${t}\``).join(',')}]`);
    }

    if (filters.isOnSale !== undefined) {
      filterParts.push(`is_on_sale:=${filters.isOnSale}`);
    }

    if (filters.priceMin !== undefined || filters.priceMax !== undefined) {
      const min = filters.priceMin ?? 0;
      const max = filters.priceMax ?? 999999;
      filterParts.push(`price:[${min}..${max}]`);
    }

    return filterParts.join(' && ');
  }

  /**
   * Fetches popular search terms from the Typesense Analytics query suggestions collection.
   * Falls back to subcategory facets if no analytics data is available yet (cold start).
   */
  async getPopularSearches(limit: number = 6): Promise<string[]> {
    try {
      const searches = await this.fetchQuerySuggestions(limit);
      if (searches.length > 0) {
        this.popularSearches.set(searches);
        return searches;
      }
      return this.fetchPopularFromFacets(limit);
    } catch {
      return this.fetchPopularFromFacets(limit);
    }
  }

  private async fetchQuerySuggestions(limit: number): Promise<string[]> {
    const result = await this.client
      .collections(this.querySuggestionsCollection)
      .documents()
      .search({
        q: '*',
        query_by: 'q',
        sort_by: 'count:desc',
        per_page: limit
      } as any);

    const raw = (result.hits || [])
      .map((hit: any) => (hit.document.q as string).replace(/[^a-zA-Z0-9\s]/g, '').trim().toLowerCase())
      .filter((q: string) => q && q !== '*' && q.length >= 3);

    // Deduplicate: remove entries that are a prefix/substring of another (higher-count) entry
    const deduped: string[] = [];
    for (const term of raw) {
      const isDuplicate = deduped.some(existing =>
        existing.includes(term) || term.includes(existing)
      );
      if (!isDuplicate) deduped.push(term);
    }

    return deduped;
  }

  private async fetchPopularFromFacets(limit: number): Promise<string[]> {
    try {
      const result = await this.client
        .collections(this.collection)
        .documents()
        .search({
          q: '*',
          query_by: 'name',
          per_page: 0,
          facet_by: 'subcategory_name',
          max_facet_values: limit
        });

      const facet = (result.facet_counts || [])
        .find((f: any) => f.field_name === 'subcategory_name');

      const searches = (facet?.counts || [])
        .sort((a: any, b: any) => b.count - a.count)
        .slice(0, limit)
        .map((c: any) => c.value as string);

      this.popularSearches.set(searches);
      return searches;
    } catch {
      this.popularSearches.set([]);
      return [];
    }
  }

  clearSearch(): void {
    this.searchQuery.set('');
    this.searchResults.set(null);
    this.suggestions.set([]);
    this.searchError.set(null);
  }

  clearSuggestions(): void {
    this.suggestions.set([]);
    this.querySuggestions.set([]);
    this.suggestionsTotalFound.set(0);
    this.suggestionsReturnedCount.set(0);
  }

  loadRecentSearches(): void {
    if (this.recentSearches().length > 0) return;
    const stored = this.localStorage.get<string[]>(RECENT_SEARCHES_STORAGE_KEY);
    if (Array.isArray(stored)) this.recentSearches.set(stored);
  }

  addRecentSearch(query: string): void {
    const q = (query || '').trim();
    if (!q) return;
    const next = [q, ...this.recentSearches().filter(x => x.toLowerCase() !== q.toLowerCase())]
      .slice(0, RECENT_SEARCHES_LIMIT);
    this.recentSearches.set(next);
    this.localStorage.set(RECENT_SEARCHES_STORAGE_KEY, next);
  }

  removeRecentSearch(query: string): void {
    const next = this.recentSearches().filter(x => x !== query);
    this.recentSearches.set(next);
    this.localStorage.set(RECENT_SEARCHES_STORAGE_KEY, next);
  }

  clearRecentSearches(): void {
    this.recentSearches.set([]);
    this.localStorage.remove(RECENT_SEARCHES_STORAGE_KEY);
  }

  /**
   * Fetches popular/featured products sorted by position.
   * Used when search bar is focused with empty query.
   */
  async getPopularProducts(limit: number = 8): Promise<SearchSuggestion[]> {
    try {
      const searchParameters: Record<string, string> = {
        q: '*',
        query_by: 'name',
        per_page: String(limit),
        // Lower position = more popular (backend cron updates this field).
        sort_by: 'position:asc'
      };

      const result = await this.client
        .collections(this.collection)
        .documents()
        .search(searchParameters);

      const products: SearchSuggestion[] = (result.hits || []).map((hit: any) => {
        const doc = hit.document as TypesenseDocument;
        const richProduct = this.parseProductData(doc);

        return {
          id: doc.id,
          name: doc.name,
          type: 'product' as const,
          img: doc.img,
          productUrl: doc.product_url,
          price: doc.price,
          originalPrice: doc.original_price,
          producerName: doc.producer_name,
          isSubscription: doc.is_subscription,
          isALaCarte: doc.is_a_la_carte,
          isBundle: richProduct?.isBundle ?? false,
          packages: richProduct?.packages,
          attributes: richProduct?.attributes,
          canQuickAdd: richProduct
            ? isSimpleOneTimeProduct(richProduct)
            : isSimpleOneTimeProduct({
                isALaCarte: doc.is_a_la_carte,
                isSubscription: doc.is_subscription,
                isBundle: false,
                attributes: [],
                packages: []
              }),
          stockInfo: richProduct?.stockInfo,
          cutOffDeliveryDays: doc.productCutOffDeliveryDays
        };
      });

      const expanded = attachStockState(expandSuggestions(products));
      this.popularProducts.set(expanded);
      return expanded;
    } catch (error) {
      this.popularProducts.set([]);
      return [];
    }
  }

  /**
   * Generates query suggestions from product names matching the search query.
   */
  private generateQuerySuggestions(query: string, hits: any[]): string[] {
    if (!query || hits.length === 0) return [];

    const queryLower = query.toLowerCase();
    const suggestions = new Set<string>();

    suggestions.add(query.toLowerCase());

    hits.forEach((hit: any) => {
      const doc = hit.document as TypesenseDocument;
      const nameLower = doc.name.toLowerCase();

      const words = nameLower.split(/\s+/);
      const queryWordIndex = words.findIndex(word => word.startsWith(queryLower) || word.includes(queryLower));

      if (queryWordIndex >= 0) {
        suggestions.add(words[queryWordIndex]);

        if (queryWordIndex + 1 < words.length) {
          suggestions.add(`${words[queryWordIndex]} ${words[queryWordIndex + 1]}`);
        }
        if (queryWordIndex > 0) {
          suggestions.add(`${words[queryWordIndex - 1]} ${words[queryWordIndex]}`);
        }
      }
    });

    return Array.from(suggestions)
      .filter(s => s.length >= query.length)
      .slice(0, 6);
  }

  /**
   * Parses the `product_data` JSON string from a Typesense document.
   * Returns null if the field is missing or invalid.
   */
  private parseProductData(doc: TypesenseDocument): TypesenseProduct | null {
    if (!doc.product_data) return null;
    try {
      return JSON.parse(doc.product_data) as TypesenseProduct;
    } catch {
      return null;
    }
  }

  private transformToProductResume(hits: any[]): ProductResume[] {
    return hits.map((hit: any) => {
      const doc = hit.document as TypesenseDocument;
      const richProduct = this.parseProductData(doc);

      if (richProduct) {
        return {
          id: richProduct.id,
          name: richProduct.name,
          price: richProduct.price,
          originalPrice: richProduct.originalPrice,
          productUrl: richProduct.productUrl,
          category: richProduct.category,
          subcategory: richProduct.subcategory,
          subcategorySon: richProduct.subcategorySon ?? { id: 0, name: '' },
          img: richProduct.img,
          isALaCarte: richProduct.isALaCarte,
          isSubscription: richProduct.isSubscription,
          tags: richProduct.tags,
          // Mapped explicitly like everything else here, so it survives the search grid: the
          // flat-field fallback below has no source for it and simply goes without.
          badges: richProduct.badges,
          attributes: richProduct.attributes,
          packages: richProduct.packages,
          display: richProduct.display,
          producer: richProduct.producer,
          categoryInView: null,
          subCategoryInView: null,
          subCategorySonInView: null,
          categoryAnchorLink: '',
          subcategoryAnchorLink: '',
          subcategorySonAnchorLink: ''
        };
      }

      // Fallback: build from flat fields if product_data is not available
      return {
        id: parseInt(doc.id, 10),
        name: doc.name,
        price: doc.price,
        originalPrice: doc.original_price,
        productUrl: doc.product_url,
        category: {
          id: parseInt(doc.category_id, 10),
          name: doc.category_name
        },
        subcategory: {
          id: parseInt(doc.subcategory_id, 10),
          name: doc.subcategory_name
        },
        subcategorySon: { id: 0, name: '' },
        img: doc.img,
        isALaCarte: doc.is_a_la_carte,
        isSubscription: doc.is_subscription,
        tags: doc.tag_names?.map((name, index) => ({
          id: doc.tag_ids?.[index] ? parseInt(doc.tag_ids[index], 10) : index,
          name,
          icon: ''
        })),
        producer: doc.producer_id ? {
          id: parseInt(doc.producer_id, 10),
          name: doc.producer_name || ''
        } : undefined,
        categoryInView: null,
        subCategoryInView: null,
        subCategorySonInView: null,
        categoryAnchorLink: '',
        subcategoryAnchorLink: '',
        subcategorySonAnchorLink: ''
      };
    });
  }

  private transformFacets(facetCounts: any[]): SearchFacet[] {
    return facetCounts.map((facet: any) => ({
      field: facet.field_name,
      counts: facet.counts.map((count: any) => ({
        value: count.value,
        count: count.count
      }))
    }));
  }
}
